import {ReactElement} from "react";
import {forwardRef, InputHTMLAttributes} from "react";
import styled from "styled-components";

import {HookFormContext, HookFormContextType} from "@/context/HookFormContext";
import {quicksandSemiBold} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";

type InputProps = InputHTMLAttributes<HTMLInputElement>;

const InputStyled = styled.input`
  &[type="text"],
  &[type="number"] {
    max-width: calc(100% - 40px);
    flex: 1;
    padding: 10px 20px;
    border: 2px solid ${({theme}) => theme.colors.mediumSecondary};
    min-width: 120px;
    ${quicksandSemiBold};
    color: ${({theme}) => theme.colors.grey};
    font-size: 0.875rem;
    &:active,
    &:focus {
      outline: 2px solid ${({theme}) => theme.colors.mediumSecondary};
    }
    &::placeholder {
      color: ${({theme}) => theme.colors.PlaceholderColor};
    }
  }
  &[type="checkbox"],
  &[type="radio"] {
    margin: 0;
    appearance: none;
    min-width: 1rem;
    height: 1rem;
    outline: 2px solid ${({theme}) => theme.colors.mediumSecondary};
    &:checked {
      background-color: ${({theme}) => theme.colors.mediumSecondary};
      outline: 2px solid white;
    }
    &:disabled {
      &:checked {
        background-color: ${({theme}) => theme.colors.shadow};
      }
      border: 2px solid white;
    }
  }
  &[type="radio"] {
    border-radius: 2rem;
  }
  &[type="date"] {
    border: 2px solid ${({theme}) => theme.colors.mediumSecondary};
    padding: 10px 20px;
    ${quicksandSemiBold};
    &:active,
    &:focus {
      outline: 2px solid ${({theme}) => theme.colors.mediumSecondary};
    }
  }
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({type = "text", ...props}, ref): ReactElement => {
    const handleWheel = (event: React.WheelEvent<HTMLInputElement>): void => {
      if (type === "number" && event.target instanceof HTMLInputElement) {
        event.target.blur();
      }
    };

    return (
      <InputStyled
        ref={ref}
        type={type}
        {...props}
        data-testid="InputStyled"
        onWheel={handleWheel}
      />
    );
  }
);

type InputLabelProps = InputProps & {
  label?: string;
  isInColumn?: boolean;
  reverseOrder?: boolean;
};

const InputLabelContentStyled = styled.div<{
  $isInColumn: boolean;
  $reverseOrder: boolean;
  $type: string;
  $isChecked?: boolean | null;
  $isDisabled?: boolean;
}>`
  display: flex;
  flex-wrap: ${({$type}) =>
    ["radio", "checkbox"].includes($type) ? "nowrap" : "wrap"};
  flex-direction: ${({$isInColumn, $reverseOrder}) =>
    $isInColumn
      ? `column${$reverseOrder ? "-reverse" : ""}`
      : `row${$reverseOrder ? "-reverse" : ""}`};
  padding: 10px 15px;
  height: fit-content;
  gap: 15px;
  border-radius: 15px;
  justify-content: ${({$type}) =>
    ["radio", "checkbox"].includes($type) ? "center" : "flex-start"};
  align-items: center;
  background-color: ${({$isChecked, $type, theme}) =>
    $isChecked === true && ["radio", "checkbox"].includes($type)
      ? theme.colors.mediumSecondary
      : "transparent"};
  user-select: none;
  &:hover,
  & label:hover,
  & input:hover {
    cursor: pointer;
  }
  &:hover {
    ${({$type, theme}) =>
      ["radio", "checkbox"].includes($type)
        ? `outline: 2px solid ${theme.colors.mediumSecondary}`
        : ""}
  }
  ${({$isDisabled, $type, theme, $isChecked}) =>
    $isDisabled &&
    `cursor: not-allowed !important;
    color: ${theme.colors.shadow};
    background-color: ${$isChecked ? theme.colors.shadow : "transparent"};
    & label {
      cursor: not-allowed !important;
      color: ${theme.colors.shadow};
    }
    & input {
      cursor: not-allowed !important;
      color: ${theme.colors.shadow};
    }
    & input[type="radio"],
    & input[type="checkbox"] {
      outline: 2px solid ${theme.colors.shadow};
    }
    &:hover {
      ${
        ["radio", "checkbox"].includes($type)
          ? "outline-color: transparent ;"
          : ""
      }
    }`}
`;
const LabelStyled = styled.label<{$isChecked?: boolean | null; $type: string}>`
  color: ${({$isChecked, $type, theme}) =>
    $isChecked && ["radio", "checkbox"].includes($type)
      ? "white"
      : theme.colors.darkPrimary};
  ${quicksandSemiBold}
`;

export const InputLabel = forwardRef<HTMLInputElement, InputLabelProps>(
  (
    {label, isInColumn = true, reverseOrder = false, type = "text", ...props},
    ref
  ): ReactElement => {
    const {watch, setValue} = useGetContext<HookFormContextType>(
      HookFormContext,
      "HookFormContext"
    );

    const isChecked = watch(props?.name || "");
    const handleClick = (): void => {
      if (
        !props?.disabled &&
        props?.name &&
        ["radio", "checkbox"].includes(type)
      ) {
        setValue(props.name, props.value);
      }
    };

    return (
      <InputLabelContentStyled
        data-testid="InputLabelContentStyled"
        $isInColumn={isInColumn}
        $reverseOrder={reverseOrder}
        $isChecked={isChecked === props?.value}
        $type={type}
        onClick={handleClick}
        $isDisabled={props?.disabled}>
        <LabelStyled
          $isChecked={isChecked === props?.value}
          $type={type}
          htmlFor={props?.id}>
          {label}
        </LabelStyled>
        <InputStyled ref={ref} type={type} {...props} />
      </InputLabelContentStyled>
    );
  }
);
